// LiveGrafX — internacionalização leve (PT / EN).
//
// Como funciona:
// - O texto em português é a própria chave: t("Salvar") → "Save" em inglês.
//   Se uma tradução faltar, o texto em português aparece (nunca quebra a tela).
// - Variáveis: t("Olá, {nome}", { nome }) — o mesmo {nome} vai no texto em inglês.
// - O idioma fica salvo no navegador (localStorage "lgfx_lang"). Na primeira visita,
//   segue o idioma do navegador (pt* → português; qualquer outro → inglês).
// - Trocar o idioma recarrega a página, para que todos os textos (inclusive os
//   definidos fora dos componentes) sejam recalculados.
import en from "./en";

export const LANGS = ["pt", "en"];
const STORAGE_KEY = "lgfx_lang";

function detect() {
  try {
    const qs = new URLSearchParams(window.location.search).get("lang");
    if (LANGS.includes(qs)) {
      try { localStorage.setItem(STORAGE_KEY, qs); } catch {}
      return qs;
    }
  } catch {}
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (LANGS.includes(saved)) return saved;
  } catch {}
  const nav = (typeof navigator !== "undefined" && (navigator.languages?.[0] || navigator.language)) || "pt";
  return nav.toLowerCase().startsWith("pt") ? "pt" : "en";
}

let current = detect();
try { document.documentElement.lang = current === "pt" ? "pt-BR" : "en"; } catch {}

export const getLang = () => current;
export const isEn = () => current === "en";
/** Locale para datas e números (Intl / toLocaleString). */
export const locale = () => (current === "en" ? "en-US" : "pt-BR");

export function setLang(lang) {
  if (!LANGS.includes(lang) || lang === current) return;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
  current = lang;
  // Remove ?lang= da URL para a escolha manual prevalecer.
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("lang");
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

const missing = new Set();

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m));
}

/** Traduz um texto em português para o idioma atual. */
export function t(pt, vars) {
  if (pt === undefined || pt === null) return pt;
  if (current === "pt") return interpolate(pt, vars);
  const tr = en[pt];
  if (tr === undefined) {
    if (process.env.NODE_ENV !== "production" && !missing.has(pt)) {
      missing.add(pt);
      // eslint-disable-next-line no-console
      console.warn("[i18n] tradução EN faltando:", pt);
    }
    return interpolate(pt, vars);
  }
  return interpolate(tr, vars);
}

/** Tradução "suave" para textos dinâmicos (vindos do servidor): traduz se houver
 *  entrada no dicionário; senão devolve o original sem avisos. */
export function tx(text) {
  if (current === "pt" || typeof text !== "string") return text;
  const tr = en[text];
  return tr === undefined ? text : tr;
}

/** Escolhe entre duas versões de um conteúdo vindo do banco: en se existir, senão pt. */
export function pick(ptValue, enValue) {
  if (current === "en" && enValue !== undefined && enValue !== null && enValue !== "") return enValue;
  return ptValue;
}

/** Formata data/hora no idioma atual (fuso de São Paulo, como no restante do app). */
export function fmtDateTime(iso, opts = { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(locale(), opts); } catch { return iso; }
}

export function fmtNumber(n) {
  try { return Number(n).toLocaleString(locale()); } catch { return String(n); }
}

export function fmtMoney(value, currency = "BRL") {
  try { return Number(value || 0).toLocaleString(locale(), { style: "currency", currency: currency || "BRL" }); }
  catch { return `${currency} ${value}`; }
}
